import React, { useState } from 'react';

// Renders emojis as Apple (iPhone) artwork instead of the platform's system font.
const APPLE_EMOJI_BASE = 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple@16.0.0/img/apple/64/';

const EMOJI_RE = /(\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier}|‍\p{Extended_Pictographic}️?)*)/gu;

const toFilename = (emoji: string, keepVs16: boolean): string =>
    Array.from(emoji)
        .map(ch => ch.codePointAt(0)!)
        .filter(cp => keepVs16 || cp !== 0xfe0f)
        .map(cp => cp.toString(16).padStart(4, '0'))
        .join('-') + '.png';

// Plain symbols such as ©, ™ or ✓ are Extended_Pictographic but should stay text.
const isRealEmoji = (s: string): boolean => {
    const cp = s.codePointAt(0)!;
    return cp >= 0x1f000 || s.includes('️') || (cp >= 0x2600 && cp <= 0x27bf && cp !== 0x2713 && cp !== 0x2717);
};

const AppleEmoji: React.FC<{ emoji: string }> = ({ emoji }) => {
    // 0: with VS16 (dataset default), 1: without, 2: give up and show native glyph
    const [attempt, setAttempt] = useState(0);
    if (attempt > 1) return <>{emoji}</>;
    return (
        <img
            src={APPLE_EMOJI_BASE + toFilename(emoji, attempt === 0)}
            alt={emoji}
            draggable={false}
            onError={() => setAttempt(a => a + 1)}
            style={{ height: '1.2em', width: '1.2em', verticalAlign: '-0.2em', display: 'inline-block' }}
        />
    );
};

export const EmojiText: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    if (typeof children !== 'string') return <>{children}</>;
    const parts = children.split(EMOJI_RE);
    if (parts.length === 1) return <>{children}</>;
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 && isRealEmoji(part) ? <AppleEmoji key={i} emoji={part} /> : <React.Fragment key={i}>{part}</React.Fragment>
            )}
        </>
    );
};
